import {map} from 'rxjs/operators';
// Angular
import {Injectable} from '@angular/core';
import {forkJoin, Observable, of as observableOf} from 'rxjs';
// Afidium
import {ISummary} from 'libraries/component/search-summary/ISummary';
import {BookingService} from 'libraries/service/booking/booking.service';
import {BookingSearchParameters, IConfig, IConfigFormArray} from 'app_sasha/shared/models/BookingSearchParameters';
import {DateRangePipe} from 'app_sasha/shared/pipes/dateRange/dateRange.pipe';
import {BookingStatusService} from 'libraries/component/booking/booking-status/booking-status.service';
import {IBookingSearchParametersURL} from 'libraries/interfaces/IBookingSearchParameters';
import {Yii} from "libraries/interfaces/Yii";
import {EnumsResourceService} from "libraries/service/booking/enums/enums-resource.service";
import {IDateRange} from "libraries/service/resources/model/search/IDateRange";
import {IGetEnumsAfidiumService} from "libraries/service/booking/enums/IGetEnums";
import {HostAliasResource} from "libraries/service/resources/host-alias/host-alias.resource";
import {ExportFormats, IApiExportResponse} from "../../../libraries/interfaces/IApiExportResponse";
import {ApiImpactOnLuggage} from "../../../libraries/service/resources/booking/interfaces/ApiSearchParameters";

@Injectable()
export class BookingSummaryService {
    private readonly unknownLabel: string = Yii.t('wui-shared-js', "Inconnu");

    constructor(
        private readonly bookingService: BookingService,
        private readonly bookingStatusService: BookingStatusService,
        private readonly dateRangePipe: DateRangePipe,
        private readonly enumsResourceService: EnumsResourceService,
        private readonly hostAliasResource: HostAliasResource,
    ) {
    }

    /**
     * This method create summary for afidium service FormArray
     * @param params URL parameters
     * @param shortName prefix of indexed parameters
     */
    public createAfidiumServiceSummaries(params: IBookingSearchParametersURL, shortName: string): Observable<ISummary[]> {
        const propertyShortName: IConfigFormArray[] = BookingSearchParameters.AFIDIUM_SERVICE_PROPERTIES;
        const maxServiceIndex: number = BookingSearchParameters.extractMaxIndexNumber(params, shortName);
        const summaryObservables: Array<Observable<ISummary>> = [];
        for (let index: number = 0; index <= maxServiceIndex; index++) {
            const stringObservables: Array<Observable<string>> = [];
            propertyShortName.forEach((propertyItem) => {
                if (propertyItem.type === 'string') {

                    if (params[`${shortName}.${index}.${propertyItem.shortName}`]) {
                        let value: string[] = params[`${shortName}.${index}.${propertyItem.shortName}`];
                        if (typeof value === 'string') {
                            value = [value];
                        }
                        stringObservables.push(
                            this.translateText(propertyItem.name, value)
                                .pipe(
                                    map((result: string) => `${propertyItem.displayValue} : ${result}`)
                                ));
                    }
                } else if (propertyItem.type === 'date') {
                    const valueMin: string = params[`${shortName}.${index}.${propertyItem.shortName}min`];
                    const valueMax: string = params[`${shortName}.${index}.${propertyItem.shortName}max`];
                    const valueDate: IDateRange = {};
                    if (valueMin) {
                        valueDate.min = valueMin;
                    }
                    if (valueMax) {
                        valueDate.max = valueMax;
                    }
                    stringObservables.push(observableOf(this.dateRangePipe.transform(valueDate)));
                }
            });
            if (maxServiceIndex !== null) {
                summaryObservables.push(forkJoin(stringObservables).pipe(
                    map((serviceStrings: string[]) => {
                        return {
                            field: `Afidium service ${index}`,
                            value: serviceStrings.join('<br/>'),
                            label: `Afidium service ${index}`,
                        };
                    })
                ));
            }
        }

        return summaryObservables.length ? forkJoin(summaryObservables) : observableOf([]);
    }

    public createSummary(params: BookingSearchParameters, fieldLabels: { [key: string]: string }, fieldPluralLabels: { [key: string]: string }): Observable<ISummary[]> {
        const textSummaries: Array<Observable<ISummary>> = BookingSearchParameters.STRING_ARRAY_PROPERTIES
            .filter((config: IConfig): boolean => params[config.name] !== undefined && params[config.name].length > 0)
            .map((config: IConfig): Observable<ISummary> => this.translateText(config.name, params[config.name])
                .pipe(
                    map((value: string): ISummary => ({
                        field: config.name,
                        value,
                        label: fieldLabels[config.name] || config.name,
                        pluralLabel:  fieldPluralLabels[config.name],
                    })))
            );
        const textSummary: Observable<ISummary[]> = textSummaries.length ? forkJoin(...textSummaries) : observableOf([]);

        const codeSummary: Observable<ISummary[]> = observableOf(
            BookingSearchParameters.CODE_TEXT_PROPERTIES
                .filter((config) => params[config.name] !== undefined
                    && params[config.name].text
                    && params[config.name].text.length > 0)
                .map((config: IConfig) => ({
                    field: config.name,
                    value: Array.isArray(params[config.name].text) ? (params[config.name].text).join(', ') : params[config.name].text,
                    label: fieldLabels[config.name],
                }))
        );

        const numberSummary: Observable<ISummary[]> = observableOf(
            BookingSearchParameters.NUMBER_PROPERTIES
                .filter((config: IConfig): boolean => Boolean(params[config.name]) || params[config.name] === 0)
                .map((config: IConfig): ISummary => ({
                    field: config.name,
                    value: params[config.name],
                    label: fieldLabels[config.name],
                }))
        );
        const percentageSummary: Observable<ISummary[]> = observableOf(
            BookingSearchParameters.PERCENTAGE_PROPERTIES
                .filter((config: IConfig): boolean => Boolean(params[config.name]) || params[config.name] === 0)
                .map((config: IConfig): ISummary => ({
                    field: config.name,
                    value: `${params[config.name]}%`,
                    label: fieldLabels[config.name],
                }))
        );

        const dateSummary: Observable<ISummary[]> = observableOf(
            BookingSearchParameters.DATE_RANGE_PROPERTIES
                .filter((config) => params[config.name] !== undefined
                    && (params[config.name].missing || params[config.name].min || params[config.name].max))
                .map((config: IConfig) => ({
                    field: config.name,
                    value: this.dateRangePipe.transform(params[config.name]),
                    label: fieldLabels[config.name],
                }))
        );

        const booleanSummary: Observable<ISummary[]> = observableOf(
            BookingSearchParameters.BOOLEAN_PROPERTIES
                .filter((config: IConfig) => params[config.name] !== undefined)
                .map((config: IConfig) => ({
                    field: config.name,
                    value: params[config.name] ? Yii.t('wui-shared-js', 'oui') : Yii.t('wui-shared-js', 'non'),
                    label: fieldLabels[config.name],
                }))
        );

        return forkJoin(
            textSummary,
            codeSummary,
            dateSummary,
            booleanSummary,
            numberSummary,
            percentageSummary,
        ).pipe(map(([textValues, codeValues, dateValues, booleanValues, numberValues, percentageValues]:
                        [ISummary[], ISummary[], ISummary[], ISummary[], ISummary[], ISummary[]]): ISummary[] =>
            [...textValues, ...codeValues, ...dateValues, ...booleanValues, ...numberValues, ...percentageValues]));
    }

    private readonly demiExport: IApiExportResponse = {
        name: 'journal_d_emission.xlsx',
        format: ExportFormats.XLSX,
        url: 'https://toto.tutu/truc'
    };

    /* public export(request: BookingSearchParameters): Observable<IApiExportResponse> {
         return of(this.dummyExport);
         return this.airTicketingRessourceService.export(request, ExportFormats.XLSX);

     }*/


    private translateText(fieldName: string, values: string[]): Observable<string> {
        let allValues: Array<Observable<string>>;
        switch (fieldName) {
            case 'xStatus':
                allValues = values.map((valueItem: string) => observableOf(this.bookingStatusService.getStatusLabel(valueItem)));
                break;
            case 'midOffice':
                allValues = values.map((valueItem: string) => this.bookingService.getMidOfficeLabel(valueItem));
                break;
            case 'xType':
                allValues = values.map((valueItem: string) => this.bookingService.getXTypeLabel(valueItem));
                break;
            case 'issueStatus':
                allValues = values.map((valueItem: string) => this.bookingService.getIssueStatusLabel(valueItem));
                break;
            case 'pricingRate':
                allValues = values.map((valueItem: string) => this.bookingService.getPricingRateLabel(valueItem));
                break;
            case 'pricingXState':
                allValues = values.map((valueItem: string) => observableOf(this.bookingService.getPricingXStateLabel(valueItem)));
                break;
            case 'serviceCode':
                allValues = values.map((valueItem: string) => this.bookingService.getServiceCodeLabel(valueItem));
                break;
            case 'serviceXStatus':
                allValues = values.map((valueItem: string) => this.bookingService.getServiceXStatusLabel(valueItem));
                break;
            case 'serviceCodes' :
                allValues = values.map((item: string) => this.findServiceList(item));
                break;
            case 'eligibilityStatuses' :
                allValues = values.map((valueItem: string) => observableOf(this.bookingService.getEligibilityLabel(valueItem)));
                break;
            case 'operationStatuses':
                allValues = values.map((valueItem: string) => observableOf(this.bookingService.getOperationLabel(valueItem)));
                break;
            case 'host':
                allValues = values.map((valueItem: string) => (this.hostAliasResource.getAlias(valueItem)));
                break;
            case 'type':
                allValues = values.map((valueItem: string) => observableOf(this.bookingService.getTypeLabel(valueItem)));
                break;
            case 'qualityCheck':
                allValues = values.map((valueItem: string) => this.bookingService.getQualityCheckLabel(valueItem));
                return forkJoin(...allValues).pipe(map((translated: string[]) => translated.join('¤¤ ')));
                break;
            case 'impactOnFareType':
                allValues = values.map((valueItem: string) => observableOf(this.bookingService.getImpactOnFareTypeLabel(valueItem)));
                break;
            case 'impactOnLuggage':
                allValues = values.map((valueItem: string) => observableOf(this.bookingService.getImpactOnLuggageLabel(valueItem)));
                break;
            case 'hasMembershipCard':
                allValues = values.map((valueItem: string) => observableOf(this.bookingService.getHasMembershipCardLabel(valueItem)));
                break;
            case 'hasSeatAssignment':
                allValues = values.map((valueItem: string) => observableOf(this.bookingService.getHasSeatAssignmentLabel(valueItem)));
                break;
            default:
                return observableOf(values.join(', '));
        }


        return forkJoin(...allValues).pipe(map((translated: string[]) => translated.join(', ')));
    }

    private findServiceList(serviceCode: string): Observable<string> {
        return this.enumsResourceService.listAfidiumServices()
            .pipe(map((datas: IGetEnumsAfidiumService[]) => {
                for (const data of datas) {
                    if (data.code === serviceCode) {
                        return data.name;
                    }
                }
                return this.unknownLabel;
            }));
    }

}
